/**
 * Learning With Errors (LWE) Explorer
 * Shows why a small error turns an easy linear system into the hard problem
 * that ML-KEM and ML-DSA rest on: Gaussian elimination amplifies the noise,
 * and the only honest way back to the secret is a closest-vector search.
 */
import {
  centered,
  makeRng,
  generateLwe,
  solveByElimination,
  residuals,
  residual,
  searchHeatmap,
  dotMod,
  type LweInstance,
} from '../../lib/labs/lwe';

// ===== DOM =====
const nSelector = document.getElementById('lwe-n-selector') as HTMLElement;
const qSelector = document.getElementById('lwe-q-selector') as HTMLElement;
const modeSelector = document.getElementById('lwe-mode-selector') as HTMLElement;
const noiseInput = document.getElementById('lwe-noise') as HTMLInputElement;
const noiseVal = document.getElementById('lwe-noise-val') as HTMLElement;
const regenerateBtn = document.getElementById('lwe-regenerate') as HTMLButtonElement;
const revealInput = document.getElementById('lwe-reveal') as HTMLInputElement;

const systemEl = document.getElementById('lwe-system') as HTMLElement;
const hintEl = document.getElementById('lwe-hint') as HTMLElement;
const elimEl = document.getElementById('lwe-elim') as HTMLElement;
const searchEl = document.getElementById('lwe-search') as HTMLElement;
const readoutEl = document.getElementById('lwe-readout') as HTMLElement;

// ===== state =====
type Mode = 'elim' | 'search';
let n = 2;
let q = 13;
const m = 8;
let noiseWidth = 1;
let reveal = true;
let mode: Mode = 'elim';
let seed = (Math.random() * 1e9) | 0;
let inst: LweInstance = generateLwe(n, m, q, noiseWidth, makeRng(seed));
let guess: number[] | null = null;

const Q_OPTIONS = [7, 11, 13, 17];
const N_OPTIONS = [2, 3];

// ===== color helpers =====
function lerpColor(
  a: [number, number, number],
  b: [number, number, number],
  t: number
): string {
  const c = a.map((v, i) => Math.round(v + (b[i] - v) * Math.max(0, Math.min(1, t))));
  return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
}

// Calm slate for small magnitude, hot red for large. Makes "this value is big"
// jump out, which is the whole point when the error gets amplified.
function magColor(value: number): string {
  const t = Math.abs(value) / (q / 2);
  return lerpColor([15, 23, 42], [239, 68, 68], t);
}

// Bright emerald for low residual (good guess), dark for high.
function residualColor(r: number, lo: number, hi: number): string {
  const t = hi > lo ? (r - lo) / (hi - lo) : 0;
  return lerpColor([52, 211, 153], [10, 16, 28], t);
}

// ===== small builders =====
function cell(text: string, bg: string, extra = ''): string {
  return `<span class="inline-flex h-8 min-w-8 items-center justify-center rounded px-1.5 font-mono text-xs text-slate-100" style="background:${bg};${extra}">${text}</span>`;
}

function vectorRow(
  label: string,
  values: number[],
  colorFn: (v: number) => string
): string {
  const cells = values.map((v) => cell(String(v), colorFn(v))).join('');
  return `<div class="flex flex-wrap items-center gap-1.5">
    <span class="w-24 shrink-0 font-mono text-xs text-muted">${label}</span>
    ${cells}
  </div>`;
}

// ===== system view =====
function renderSystem(): void {
  const head = `<div class="flex flex-wrap items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-muted">
      <span class="w-24 shrink-0">sample</span>
      ${Array.from({ length: n }, (_, j) => `<span class="inline-flex h-5 min-w-8 items-center justify-center">a${j + 1}</span>`).join('')}
      <span class="inline-flex h-5 min-w-8 items-center justify-center text-accent">b</span>
      ${reveal ? '<span class="ml-2 inline-flex h-5 min-w-8 items-center justify-center">&#10216;a,s&#10217;</span><span class="inline-flex h-5 min-w-8 items-center justify-center">+e</span>' : ''}
    </div>`;

  const rows = inst.A.map((row, i) => {
    const aCells = row.map((v) => cell(String(v), magColor(centered(v, q)))).join('');
    const bCell = cell(String(inst.b[i]), 'rgba(96,165,250,0.25)');
    let extra = '';
    if (reveal) {
      const as = dotMod(inst.A[i], inst.s, q);
      const ei = centered(inst.e[i], q);
      extra =
        '<span class="ml-2">' +
        cell(String(as), 'rgba(148,163,184,0.15)') +
        '</span>' +
        cell((ei >= 0 ? '+' : '') + ei, magColor(ei));
    }
    return `<div class="flex flex-wrap items-center gap-1.5">
        <span class="w-24 shrink-0 font-mono text-xs text-muted">eq ${i + 1}</span>
        ${aCells}${bCell}${extra}
      </div>`;
  }).join('');

  const secret = reveal
    ? `<div class="mt-3 rounded-lg border border-purple-500/40 bg-purple-500/10 p-3 font-mono text-sm text-purple-200">secret s = (${inst.s.join(', ')})</div>`
    : `<div class="mt-3 rounded-lg border border-border/70 bg-surface/50 p-3 font-mono text-sm text-muted">secret s = hidden &mdash; that is the point</div>`;

  systemEl.innerHTML = `<div class="space-y-1.5">${head}${rows}</div>${secret}`;
}

// ===== elimination mode =====
function renderElim(): void {
  searchEl.style.display = 'none';
  elimEl.style.display = '';

  const res = solveByElimination(inst);
  if (!res.recovered || !res.inverse) {
    elimEl.innerHTML =
      '<p class="text-sm text-muted">The leading block was singular. Regenerate.</p>';
    return;
  }

  const headMat = inst.A.slice(0, n)
    .map((row) =>
      vectorRow(
        '',
        row.map((v) => v),
        (v) => magColor(centered(v, q))
      )
    )
    .join('');
  const invMat = res.inverse
    .map((row) => vectorRow('', row, (v) => magColor(centered(v, q))))
    .join('');

  const errorRow = vectorRow('error e (used)', res.errorSub, magColor);
  const amplifiedRow = vectorRow('after elimination', res.amplified, magColor);
  const recoveredRow = vectorRow(
    'recovered s',
    res.recovered,
    () => 'rgba(148,163,184,0.15)'
  );
  const trueRow = vectorRow('true s', inst.s, () => 'rgba(168,85,247,0.25)');

  const verdict = res.exact
    ? `<div class="rounded-lg border border-emerald-500/50 bg-emerald-500/10 p-4 text-sm text-emerald-300">
         Recovered the secret exactly. With no error, LWE is just a linear system.
       </div>`
    : `<div class="rounded-lg border border-orange-500/50 bg-orange-500/10 p-4 text-sm text-orange-300">
         Wrong secret. The tiny error became <span class="font-mono">(${res.amplified.join(', ')})</span>
         after elimination: multiplying rows by constants mod ${q} smears a small error across all of Z<sub>${q}</sub>.
       </div>`;

  elimEl.innerHTML = `
    <div class="grid gap-6 lg:grid-cols-2">
      <div>
        <p class="text-xs uppercase tracking-[0.3em] text-muted">Leading ${n}&times;${n} block of A</p>
        <div class="mt-2 space-y-1.5">${headMat}</div>
        <p class="mt-4 text-xs uppercase tracking-[0.3em] text-muted">Its inverse mod ${q}</p>
        <div class="mt-2 space-y-1.5">${invMat}</div>
        <p class="mt-2 text-xs text-muted">Solving the system means multiplying b by this inverse.</p>
      </div>
      <div class="space-y-3">
        <p class="text-xs uppercase tracking-[0.3em] text-muted">What the error does</p>
        ${errorRow}
        ${amplifiedRow}
        <div class="h-px bg-border/70"></div>
        ${recoveredRow}
        ${trueRow}
        ${verdict}
      </div>
    </div>`;

  setReadout([
    ['Mode', 'Solve by Gaussian elimination'],
    ['Noise width', noiseWidth === 0 ? '0 (no error)' : `+/- ${noiseWidth}`],
    ['Outcome', res.exact ? 'Exact recovery' : 'Failed: error amplified'],
  ]);

  hintEl.textContent =
    noiseWidth === 0
      ? 'No error: elimination recovers the secret instantly. Now raise the noise slider.'
      : 'Small error in, garbage out. Elimination is the wrong tool once there is noise.';
}

// ===== search mode =====
function bestCandidateAt(s0: number, s1: number): number[] {
  if (n === 2) return [s0, s1];
  // n === 3: pick the third coordinate that minimizes the residual.
  let best = [s0, s1, 0];
  let bestR = Infinity;
  for (let v = 0; v < q; v++) {
    const r = residual(inst, [s0, s1, v]);
    if (r < bestR) {
      bestR = r;
      best = [s0, s1, v];
    }
  }
  return best;
}

function renderSearch(): void {
  elimEl.style.display = 'none';
  searchEl.style.display = '';

  const result = searchHeatmap(inst);
  if (!result) {
    searchEl.innerHTML =
      '<p class="text-sm text-muted">Search space too large for this view.</p>';
    return;
  }

  let lo = Infinity;
  let hi = -Infinity;
  for (const row of result.grid) {
    for (const r of row) {
      if (r < lo) lo = r;
      if (r > hi) hi = r;
    }
  }

  let cells = '';
  for (let s1 = q - 1; s1 >= 0; s1--) {
    for (let s0 = 0; s0 < q; s0++) {
      const r = result.grid[s0][s1];
      const isTrue = s0 === inst.s[0] && s1 === inst.s[1];
      const isGuess = guess && guess[0] === s0 && guess[1] === s1;
      const ring = isGuess
        ? 'outline:2px solid #f87171;outline-offset:-2px;'
        : isTrue && reveal
          ? 'outline:2px solid #a855f7;outline-offset:-2px;'
          : '';
      cells += `<button type="button" data-s0="${s0}" data-s1="${s1}" title="s=(${s0}, ${s1}) residual ${r}" class="lwe-heat-cell" style="background:${residualColor(r, lo, hi)};${ring}"></button>`;
    }
  }

  const guessInfo = guess
    ? renderGuessInfo()
    : '<p class="text-sm text-muted">Click a cell to test that secret. The darkest-green cell is the global minimum.</p>';

  searchEl.innerHTML = `
    <div class="grid gap-6 lg:grid-cols-[auto_1fr]">
      <div>
        <div class="grid gap-0.5" style="grid-template-columns: repeat(${q}, minmax(0, 1fr)); width: min(${q * 26}px, 100%);">
          ${cells}
        </div>
        <div class="mt-2 flex justify-between font-mono text-[10px] text-muted">
          <span>s&#8321; = 0 &rarr; ${q - 1} (x axis)</span>
          <span>s&#8322; up (y axis)</span>
        </div>
      </div>
      <div class="space-y-3">${guessInfo}</div>
    </div>`;

  for (const btn of searchEl.querySelectorAll<HTMLButtonElement>('.lwe-heat-cell')) {
    btn.addEventListener('click', () => {
      const s0 = parseInt(btn.dataset.s0!, 10);
      const s1 = parseInt(btn.dataset.s1!, 10);
      guess = bestCandidateAt(s0, s1);
      renderSearch();
    });
  }

  setReadout([
    ['Mode', 'Brute-force closest secret'],
    ['Search space q^n', `${q}^${n} = ${result.searchSize.toLocaleString()} secrets`],
    ['Min residual at', `s = (${result.best.s.join(', ')})`],
  ]);

  hintEl.textContent =
    n === 3
      ? 'Heatmap shows the best residual over the third coordinate. The secret is still the global minimum.'
      : 'Each cell scores one candidate secret. The secret is the unique low-residual spot in the noise.';
}

function renderGuessInfo(): string {
  if (!guess) return '';
  const res = residuals(inst, guess);
  const total = res.reduce((acc, r) => acc + Math.abs(r), 0);
  const consistent = res.every((r) => Math.abs(r) <= noiseWidth);
  const isSecret = guess.every((v, i) => v === inst.s[i]);
  const rows = res
    .map(
      (r) =>
        `<span class="inline-flex h-7 min-w-8 items-center justify-center rounded px-1.5 font-mono text-xs text-slate-100" style="background:${magColor(r)}">${r >= 0 ? '+' : ''}${r}</span>`
    )
    .join('');
  const banner = isSecret
    ? `<div class="rounded-lg border border-emerald-500/50 bg-emerald-500/10 p-3 text-sm text-emerald-300">That is the secret. Every residual is within the noise bound (&plusmn;${noiseWidth}).</div>`
    : consistent
      ? `<div class="rounded-lg border border-amber-500/50 bg-amber-500/10 p-3 text-sm text-amber-300">All residuals are small, so this guess is hard to rule out from a few samples. More equations sharpen the minimum.</div>`
      : `<div class="rounded-lg border border-orange-500/50 bg-orange-500/10 p-3 text-sm text-orange-300">A wrong guess: residuals blow up to random size mod ${q}.</div>`;
  return `
    <p class="text-xs uppercase tracking-[0.3em] text-muted">Guess s = (${guess.join(', ')})</p>
    <div class="flex flex-wrap gap-1.5">${rows}</div>
    <p class="font-mono text-xs text-muted">total residual = ${total}</p>
    ${banner}`;
}

// ===== readout =====
function setReadout(rows: [string, string][]): void {
  readoutEl.innerHTML = rows
    .map(
      ([k, v]) =>
        `<div class="flex flex-col gap-0.5 rounded-lg border border-border/70 bg-surface/50 p-3">
          <span class="text-xs uppercase tracking-[0.2em] text-muted">${k}</span>
          <span class="font-mono text-sm text-text">${v}</span>
        </div>`
    )
    .join('');
}

// ===== orchestration =====
function rebuildInstance(newSeed = false): void {
  if (newSeed) seed = (Math.random() * 1e9) | 0;
  inst = generateLwe(n, m, q, noiseWidth, makeRng(seed));
  guess = null;
}

function render(): void {
  renderSystem();
  if (mode === 'elim') renderElim();
  else renderSearch();
}

function setActive(container: HTMLElement, attr: string, value: string): void {
  for (const btn of container.querySelectorAll<HTMLButtonElement>('button')) {
    btn.classList.toggle('active', btn.dataset[attr] === value);
  }
}

function buildSelectors(): void {
  nSelector.innerHTML = '';
  for (const opt of N_OPTIONS) {
    const btn = document.createElement('button');
    btn.dataset.n = String(opt);
    btn.className =
      'opt-btn rounded-full border border-border/70 px-4 py-2 text-sm text-muted transition hover:border-blue-500/50 hover:text-blue-400';
    btn.textContent = `n = ${opt}`;
    btn.addEventListener('click', () => {
      n = opt;
      rebuildInstance();
      setActive(nSelector, 'n', String(opt));
      render();
    });
    nSelector.appendChild(btn);
  }

  qSelector.innerHTML = '';
  for (const opt of Q_OPTIONS) {
    const btn = document.createElement('button');
    btn.dataset.q = String(opt);
    btn.className =
      'opt-btn rounded-full border border-border/70 px-4 py-2 text-sm text-muted transition hover:border-blue-500/50 hover:text-blue-400';
    btn.textContent = `q = ${opt}`;
    btn.addEventListener('click', () => {
      q = opt;
      rebuildInstance();
      setActive(qSelector, 'q', String(opt));
      render();
    });
    qSelector.appendChild(btn);
  }

  const modes: [Mode, string][] = [
    ['elim', 'Solve by elimination'],
    ['search', 'Search the secret'],
  ];
  modeSelector.innerHTML = '';
  for (const [id, label] of modes) {
    const btn = document.createElement('button');
    btn.dataset.mode = id;
    btn.className =
      'mode-btn rounded-full border border-border/70 px-4 py-2 text-sm text-muted transition hover:border-blue-500/50 hover:text-blue-400';
    btn.textContent = label;
    btn.addEventListener('click', () => {
      mode = id;
      setActive(modeSelector, 'mode', id);
      render();
    });
    modeSelector.appendChild(btn);
  }
}

function bindControls(): void {
  noiseInput.addEventListener('input', () => {
    noiseWidth = parseInt(noiseInput.value, 10);
    noiseVal.textContent = noiseWidth === 0 ? '0' : `+/- ${noiseWidth}`;
    rebuildInstance(); // same secret and A, fresh errors
    render();
  });
  regenerateBtn.addEventListener('click', () => {
    rebuildInstance(true);
    render();
  });
  revealInput.addEventListener('change', () => {
    reveal = revealInput.checked;
    render();
  });
}

function init(): void {
  buildSelectors();
  bindControls();
  setActive(nSelector, 'n', String(n));
  setActive(qSelector, 'q', String(q));
  setActive(modeSelector, 'mode', mode);
  noiseInput.value = String(noiseWidth);
  noiseVal.textContent = `+/- ${noiseWidth}`;
  revealInput.checked = reveal;
  render();
}

init();
